/**
 * Integrytas Group — Record Extension Registry (platform)
 *
 * A derived app routinely needs to hang its OWN attributes off a BaseApp entity:
 * SOFSEK's monitored-subject fields on csm_user_master, an app's commercial terms
 * on csm_application_master, and so on. Forking the inherited page to add them is
 * forbidden (§16 — every edit to a shared file is a merge conflict), and DSE/UDC
 * shadow-fields only carry loose scalars in jsonb: they cannot express a real FK,
 * a UNIQUE business reference, a column an RLS predicate joins on, or a child
 * collection.
 *
 * So an app declares a MANIFEST — plain data, never JSX — describing a 1:1 sister
 * table and its children, and the platform renders it generically into the host
 * page's grid, filter bar, related-children panel and edit modal.
 *
 *   registerRecordExtension({
 *     key: 'sfk_subject',
 *     page_code: 'CSM.USER.LIST',
 *     entity: 'csm_user_master',
 *     sister: { table: 'sfk_subject_profile', pk: 'user_id' },
 *     fields: [ ... ],
 *     children: [ ... ],
 *   });
 *
 * Why data and not a React component:
 *   • BaseApp improvements (a new field kind, atomic saves, count-bearing facets)
 *     reach every derived app with ZERO app changes.
 *   • The §17 screen conventions are enforced by the RENDERER, not by each app
 *     remembering them — FK/lookup labels resolved through `_tl` (B4), facets
 *     (B5), Y/N as badges (B10), related children (B13), personalization wrapping.
 *     An app cannot ship a non-conformant extension.
 *   • A manifest is small enough to scaffold, and derivable from the schema.
 *
 * INVARIANT: BaseApp ships ZERO manifests and holds no list of applications or
 * their fields. The registry is populated purely by module-load side effect from
 * each app's `src/app/extensions/*`, exactly like registerUdcHost(). With nothing
 * registered every consumer is inert — no query, no column, no behaviour change
 * (§1.5, additive & fail-open).
 */

/** Scalar column kinds the generic renderer knows how to display and edit. */
export type ExtensionFieldKind = 'text' | 'number' | 'date' | 'yn' | 'lookup' | 'fk';

/** A `<code>` + `<code>_tl` lookup pair (§8 Translation-Mapping). */
export interface ExtensionLookupRef {
  /** Definition table holding the immutable code. */
  table: string;
  /** Its `_tl` translation table (locale_code + display_text). */
  tl: string;
  /** Code column on both tables. Defaults to the field's own `key`. */
  code_column?: string;
}

/** A surrogate-key FK resolved to a human label (§17.B4 — never show the id). */
export interface ExtensionFkRef {
  table: string;
  pk: string;
  /** Column carrying the display name (e.g. 'name', 'display_text'). */
  label_column: string;
}

interface FieldCommon {
  /** Column name on the sister table. */
  key: string;
  /** i18n key + English default, so an app renders without any bundle (§16). */
  label_key: string;
  label_default: string;
  /** Append a column to the host grid. */
  grid?: boolean;
  /** Append a multi-value facet to the host filter bar (§17.B5). */
  facet?: boolean;
  required?: boolean;
  /** Business references are set once and never re-keyed. */
  immutable_after_create?: boolean;
  grid_width?: number;
}

export type ExtensionField =
  | (FieldCommon & { kind: 'text' | 'number' | 'date' | 'yn' })
  | (FieldCommon & { kind: 'lookup'; lookup: ExtensionLookupRef })
  | (FieldCommon & { kind: 'fk'; ref: ExtensionFkRef });

/** Typed structured addresses for the host record (§17.B8). Editable. */
export interface ExtensionAddressChild {
  kind: 'address';
  key: string;
  label_key: string;
  label_default: string;
  table: string;
  /** PK of the address table. */
  pk: string;
  /** Column on the address table pointing back at the host record. */
  fk: string;
  types: readonly string[];
}

/** A read-only child collection shown in the related-children panel (§17.B13). */
export interface ExtensionRelatedChild {
  kind: 'related';
  key: string;
  label_key: string;
  label_default: string;
  table: string;
  pk: string;
  fk: string;
  /** Column carrying each item's label — never a raw id (§17.B4). */
  label_column: string;
}

/** A read-only many-to-many collection reached through a junction table. */
export interface ExtensionRelatedViaChild {
  kind: 'related_via';
  key: string;
  label_key: string;
  label_default: string;
  junction: string;
  junction_pk: string;
  /** Junction column pointing back at the host record. */
  fk: string;
  /** Junction column pointing at the target. */
  target_fk: string;
  /** Target definition table + its `_tl`, so items show translated names. */
  target: ExtensionLookupRef & { pk: string };
}

export type ExtensionChild =
  | ExtensionAddressChild
  | ExtensionRelatedChild
  | ExtensionRelatedViaChild;

export interface RecordExtension {
  /** Unique within a page; also the React key and the storage namespace. */
  key: string;
  /** Host page's internal_code, e.g. 'CSM.USER.LIST'. */
  page_code: string;
  /** Host entity, e.g. 'csm_user_master'. */
  entity: string;
  /**
   * The 1:1 sister table. Its PK IS the host's PK (same value), which is what
   * makes the extension a true extension rather than a parallel record.
   */
  sister: { table: string; pk: string };
  fields: ExtensionField[];
  children?: ExtensionChild[];
  /**
   * Section heading in the host's edit modal. Omit to use the app prefix.
   */
  section_label_key?: string;
  section_label_default?: string;
}

// Held on globalThis so a single registry is shared even if the bundler or Vite
// HMR loads this module as more than one instance — same rationale as udc_hosts.
const STORE_KEY = '__integrytas_record_extensions__';

function store(): Map<string, RecordExtension> {
  const g = globalThis as unknown as Record<string, unknown>;
  if (!(g[STORE_KEY] instanceof Map)) g[STORE_KEY] = new Map<string, RecordExtension>();
  return g[STORE_KEY] as Map<string, RecordExtension>;
}

/**
 * Declare an extension (call at module load from the app's src/app/extensions).
 * Re-registering the same page+key replaces it, so HMR does not accumulate.
 */
export function registerRecordExtension(ext: RecordExtension): void {
  if (!ext.page_code || !ext.key || !ext.sister?.table) return;
  store().set(`${ext.page_code}|${ext.key}`, ext);
}

/** Every extension registered for a host page (empty in BaseApp itself). */
export function getRecordExtensions(page_code: string): RecordExtension[] {
  return [...store().values()].filter(e => e.page_code === page_code);
}

/** Prefix a manifest column so extension keys can never collide with host ones. */
export function extColumnKey(ext_key: string, field_key: string): string {
  return `ext:${ext_key}:${field_key}`;
}

/** Inverse of extColumnKey; null when the key is not an extension column. */
export function parseExtColumnKey(key: string): { ext_key: string; field_key: string } | null {
  if (!key.startsWith('ext:')) return null;
  const [, ext_key, ...rest] = key.split(':');
  if (!ext_key || rest.length === 0) return null;
  return { ext_key, field_key: rest.join(':') };
}

/** The lookup/target code column, defaulting to the field's own key. */
export function lookupCodeColumn(ref: ExtensionLookupRef, field_key: string): string {
  return ref.code_column ?? field_key;
}
